import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUpdateMutation, useGetAdminCountQuery } from '../../app/services/users';
import { useTranslation } from 'react-i18next';
import { fetchUsers } from '../../features/users/usersSlice';
import { getLanguages, getPermissions } from '../../constants/options';
import { commonTextFieldStyles } from '../../utils/textFieldStyles';
import { iconButtonStyles } from '../../utils/iconButtonStyles';
import ModalComponent from '../ModalComponent';

import {
  MenuItem,
  TextField,
  Tooltip,
  IconButton,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { ModalButton, ModalDialogActions, ModalErrorDisplay } from '../ModalComponents';
import ConfirmDiscardDialog from '../ConfirmDiscardDialog';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';

import styles from '../ModalComponent/modalStyles.module.css';

import { validateFormData, getValidationErrorMessage } from '../../helpers';
import { putUserSchema } from '../../schemas/putUserSchema';
import { selectUserById } from '../../features/users/usersSlice';

const initialForm = {
  firstname: 'firstname',
  lastname: 'lastname',
  email: 'email',
  language: 'english',
  role: 'admin',
  status: 'active',
};

export default function ModalUpdateUser({ id }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);

  const { t } = useTranslation();
  const permissions = getPermissions(t);
  const languages = getLanguages(t);

  const dispatch = useDispatch();
  const [updateUser] = useUpdateMutation();

  const [formValidationErrors, setFormValidationErrors] = useState(null);
  const [errorResponse, setErrorResponse] = useState('');
  const firstNameError = getValidationErrorMessage(formValidationErrors, 'firstname');
  const lastNameError = getValidationErrorMessage(formValidationErrors, 'lastname');
  const emailError = getValidationErrorMessage(formValidationErrors, 'email');
  const languageError = getValidationErrorMessage(formValidationErrors, 'language');
  const roleError = getValidationErrorMessage(formValidationErrors, 'role');

  const selectedUser = useSelector((state) => selectUserById(state, id));
  useEffect(() => {
    if (!selectedUser) return;
    setForm(selectedUser);
  }, [selectedUser]);

  const isDirty = open && selectedUser && JSON.stringify(form) !== JSON.stringify(selectedUser);

  const { data: adminCountData } = useGetAdminCountQuery(undefined, { skip: !open });
  const isLastAdmin =
    selectedUser?.role === 'admin' &&
    selectedUser?.status === 'active' &&
    adminCountData?.activeAdmins === 1;
  const { guardClose, confirmProps } = useUnsavedChangesWarning(isDirty, open);

  const handleOpen = () => setOpen(true);
  const doClose = () => {
    setOpen(false);
    setFormValidationErrors(null);
    setErrorResponse('');
  };
  const handleClose = () => guardClose(doClose);

  const handleChange = (e) => {
    const { name, value } = e.target;

    switch (name) {
      case 'role':
        setForm({
          ...form,
          role: value,
        });
        break;
      case 'language':
        setForm({
          ...form,
          language: value,
        });
        break;
      default:
        setForm({
          ...form,
          [name]: value,
        });
    }
  };

  const handleSubmit = async () => {
    try {
      const { id, ...userData } = form;

      const validationErrors = validateFormData(t, putUserSchema, userData);
      if (!!validationErrors) {
        setFormValidationErrors(validationErrors);
        return;
      }

      await updateUser({ id, userData }).unwrap();
      dispatch(fetchUsers());
      doClose();
    } catch (error) {
      const key = error?.data?.message;
      const known = key && t(`errors.${key}`, { defaultValue: '' });
      setErrorResponse(known || key || t('errors.generic'));
      console.error('Error updating user:', error);
    }
  };

  return (
    <div>
      <Tooltip title={t('generic.edit')} placement="top">
        <IconButton
          color="primary"
          onClick={handleOpen}
          style={iconButtonStyles}
        >
          <EditIcon />
        </IconButton>
      </Tooltip>
      <ModalComponent open={open} handleClose={handleClose}>
        <div className={styles.modal}>
          <DialogTitle sx={{ m: 0, p: 2 }}>
            {t('modals.updateUser')}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
          
          <DialogContent dividers sx={{ p: 3 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <TextField
                label={t('generic.firstName')}
                variant="outlined"
                name="firstname"
                value={form.firstname}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                error={!!firstNameError}
                helperText={firstNameError ?? ' '}
                sx={commonTextFieldStyles}
              />
              <TextField
                label={t('generic.lastName')}
                variant="outlined"
                name="lastname"
                value={form.lastname}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                error={!!lastNameError}
                helperText={lastNameError ?? ' '}
                sx={commonTextFieldStyles}
              />
              <TextField
                label={t('generic.email')}
                variant="outlined"
                name="email"
                value={form.email}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                error={!!emailError}
                helperText={emailError ?? ' '}
                sx={commonTextFieldStyles}
              />
              <TextField
                select
                label={t('modals.languagePreferences')}
                name="language"
                value={form.language}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                error={!!languageError}
                helperText={languageError ?? ' '}
                sx={commonTextFieldStyles}
              >
                {languages.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={t('generic.permission')}
                name="role"
                value={form.role}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                error={!!roleError}
                helperText={roleError || (isLastAdmin ? t('errors.cannotRemoveLastAdmin') : ' ')}
                sx={commonTextFieldStyles}
                disabled={isLastAdmin}
              >
                {permissions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </div>
          </DialogContent>

          <ModalDialogActions>
            <ModalButton
              variant="outlined"
              color="primary"
              onClick={handleClose}
            >
              {t('modals.cancel')}
            </ModalButton>
            <ModalButton
              variant="contained"
              color="primary"
              onClick={handleSubmit}
            >
              {t('modals.update')}
            </ModalButton>
          </ModalDialogActions>

          <ModalErrorDisplay errorResponse={errorResponse} />
        </div>
      </ModalComponent>
      <ConfirmDiscardDialog {...confirmProps} />
    </div>
  );
}
